import { makeStyles } from 'tss-react/mui';
import React, { useRef } from 'react';
import Icon, { AvailableIconTypes } from '@ui/Icon';
import CheckboxInput from './CheckboxInput';

export interface IconCheckboxInputProps {
  id: string;
  type: AvailableIconTypes;
  checked: boolean;
  onSelect?: (selected: boolean) => void;
  disableCheckbox?: boolean;
}

const useStyles = makeStyles()(theme => ({
  icon: {
    display: 'block',
  },
  checkbox: {
    padding: 0,
    visibility: 'hidden',
    '&.Mui-focusVisible': {
      borderRadius: theme.spacing(0.5),
      outlineOffset: 0,
    },
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    '&:hover, &:focus-within': {
      outline: 0,
      '& > .wsl-icon': {
        display: 'none',
      },
      '& > .wsl-checkbox': {
        visibility: 'visible',
      },
    },
  },
  checked: {
    visibility: 'visible',
  },
  hide: {
    display: 'none',
  },
}));

/**
 * Checkbox which displays as an icon in default state
 * On hover and focus, checkbox will appear and be selectable
 */
const IconCheckboxInput = (props: IconCheckboxInputProps) => {
  const { id, type, checked, onSelect, disableCheckbox } = props;
  const { classes, cx } = useStyles();
  const checkboxRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLInputElement>(null);

  const handleClick: React.MouseEventHandler = e => {
    e.stopPropagation();
    if (onSelect) onSelect(!checked);
  };

  /**
   * Using a parent div to capture focus, disabling eslint rule
   * because downstream we are bringing focus into the interactive element the
   * visibly hidden checkbox, so breaking rule with intention
   */
  return (
    <div
      ref={containerRef}
      className={classes.container}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
    >
      <Icon
        className={cx(classes.icon, 'wsl-icon', {
          [classes.hide]: checked,
        })}
        type={type}
      />
      <CheckboxInput
        id={`${id}-select`}
        inputRef={checkboxRef}
        className={cx(classes.checkbox, 'wsl-checkbox', {
          [classes.checked]: checked,
        })}
        checked={checked}
        onClick={handleClick}
        disabled={disableCheckbox}
        aria-label="Select item"
      />
    </div>
  );
};

export default IconCheckboxInput;
